import React, { useState, useEffect, useCallback, useMemo, memo } from 'react';
import { Table, TableProps } from 'tea-component';
import { SortBy } from "tea-component/lib/table/addons";
import { useHistory } from 'umi';
import { MENU_ID_MAP, SLASH_G_POP } from '@gp/constants/const';
import { getColumns } from './utils/common';
import { getNumberOfDotUnit } from '@gp/utils/common';
import classNames from 'classnames';
import './index.scss';

const { autotip, sortable, pageable, scrollable } = Table.addons;

interface CostTablePropsType {
  loading: boolean;
  data: IStudioOverallProjectsCostData;
  setSortsParams: React.Dispatch<React.SetStateAction<SortBy<any>>> | null,
}

const DEFAULT_PROPS: CostTablePropsType = {
  loading: true,
  data: null,
  setSortsParams: null,
};

function CostTable(props: CostTablePropsType) {
  const {
    loading: propsLoading,
    data,
    setSortsParams,
  } = props;

  const history = useHistory();
  const [records, setRecords] = useState<any[]>([]);
  const [isClickSort, setIsClickSort] = useState<boolean>(false);
  const [sorts, setSorts] = useState<SortBy<any>>({by: '', order: 'desc'});
  const [paginationMsg, setPaginationMsg] = useState<{
    pageIndex: number,
    pageSize: number,
    total: number,
  }>({
    pageIndex: 1,
    pageSize: 20,
    total: 0,
  });
  const [loading, setLoading] = useState<boolean>(false);
  // 表格是否出现纵向滚动条
  const [tableIsScroll, setTableIsScroll] = useState<boolean>(false);

  // 是否没权限
  /* const isNoPermission = useMemo(() => {
    return data?.no_auth && !loading;
  }, [data?.no_auth, loading]); */

  // 初始化整理数据
  const orgRecords = useMemo(() => {
    const newRecords = data?.data?.map((item: any, index: number) => {
      return {
        uniqueId: item?.project_detail?.id + '_' + index,
        ...item,
      }
    }) || [];
    setPaginationMsg(Object.assign(paginationMsg, {total: newRecords?.length || 0}));

    return newRecords;
  }, [propsLoading]);

  // 虚假的loading，目的是为了让横纵滚动条回到初始位置
  const resetScroll = useCallback(
    () => {
      setLoading(true);
      const t = setTimeout(() => {
        clearTimeout(t);
        setLoading(false);
      }, 50);
    },
    [],
  );

  const toDetails = useCallback((item, e, isJump = true) => {
    if (!item?.project_detail?.id) return '';
    const filter = encodeURIComponent(JSON.stringify({
      project: item?.project_detail?.name,
      projectId: item?.project_detail?.id,
      tab: 'finance',
      menuFrom: MENU_ID_MAP['pipeline_all-projects'], // 用于标识左侧菜单栏当前选中高亮
    }));
    const url = SLASH_G_POP + `/Pipeline/ProjectList/Detail?filter=${filter}`;
    if (isJump) {
      e?.preventDefault();
      e?.stopPropagation();
      history.push(url);
    }

    return url;
  }, []);

  useEffect(() => {
    setLoading(propsLoading);

    if (!propsLoading) {
      setTimeout(() => {
        const tableBody = document.querySelector('.dbg-studio-overall-projects-dev-cost-table .tea-table__body');
        // @ts-ignore
        const flag = tableBody?.clientHeight < tableBody?.firstChild?.clientHeight;
        setTableIsScroll(flag);
      });
    }
  }, [propsLoading]);

  const pageSizeOptions = [20, 50, 100];
  const showPageableOperation = paginationMsg?.total > Math.min(...pageSizeOptions);
  // 表格使用到的插件
  const addons = [
    pageable({
      stateTextVisible: true,
      pageSizeVisible: showPageableOperation,
      pageIndexVisible: showPageableOperation,
      jumpVisible: showPageableOperation,
      endJumpVisible: showPageableOperation,
      pageIndex: paginationMsg?.pageIndex,
      pageSize: paginationMsg?.pageSize,
      pageSizeOptions,
      recordCount: paginationMsg?.total,
      stateText: `${getNumberOfDotUnit(paginationMsg?.total)} item${paginationMsg?.total > 1 ? 's' : ''} in total`,
      onPagingChange: ({pageIndex, pageSize}) => {
        setPaginationMsg({...paginationMsg, pageIndex: pageIndex as number, pageSize: pageSize as number});
        resetScroll();
      },
    }),
    sortable({
      columns: [
        'bo',
        'total_budget',
        'total_spent_to_date',
        'spent_to_date_percent',
        'year_budget',
        'year_spent_to_date',
        'year_spent_to_date_percent',
        'month_spent',
      ],
      value: sorts ? [sorts] : [],
      onChange: (value) => {
        setIsClickSort(true);
        setSorts(value?.[0]);
        setSortsParams?.(value?.[0]);
        setPaginationMsg({...paginationMsg, pageIndex: 1});
        // resetScroll();
      },
    }),
    /* scrollable({
      maxHeight: 500,
    }), */
    autotip({
      isLoading: loading,
      hideIcon: !loading,  // loading状态显示图标，其他情况不显示
      emptyText: /* isNoPermission ? 'No permissions.' : */ 'The list has no data yet.',
      style:{
        height: 172,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        boxSizing: 'border-box',
      },
    }),
  ];

  useEffect(() => {
    if (!Array.isArray(orgRecords) || !orgRecords?.length) {
      setRecords([]);
      return;
    }
    setRecords([...orgRecords]);
  }, [orgRecords]);

  return (
    <section className="font14">
      <Table
        className={classNames({
          'dbg-common-table dbg-studio-overall-projects-dev-cost-table dbg-table-sticky sort-icon-color dbh-action-state-center-table': true,
          'scroll-table-header': tableIsScroll,
        })}
        // verticalTop
        records={(loading /* || isNoPermission */) ? [] : records}
        recordKey="uniqueId"
        columns={getColumns(
          toDetails,
          data?.update_time?.substring(0, 4),
          data?.update_time?.substring(5, 7),
        ) as TableProps['columns']}
        addons={addons}
      />
    </section>
  );
}

CostTable.defaultProps = DEFAULT_PROPS;

export default memo(CostTable);
